import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { FormattedMessage, useIntl } from 'react-intl';
import { FormProvider, useWatch } from 'react-hook-form';
import { useStockAiIntake } from './stock-ai-intake.hooks';
import { useCurrency } from '@features/currency/currency.hooks';
import TextField from '@shared/components/TextField';
import SelectField from '@shared/components/SelectField';
import ToggleButtonGroup from '@shared/components/ToggleButtonGroup';
import { unitList } from '@shared/entity';
import { isSubscriptionExpert } from '@shared/entity';
import { useSubscriptionPlan } from '@shared/store';

const UNITS_WITH_SUSPICIOUS_LOW_QUANTITY = new Set(['g', 'mg', 'ml', 'cm', 'mm']);

export const StockAiIntake = () => {
  const intl = useIntl();
  const { preferredCurrency } = useCurrency();
  const plan = useSubscriptionPlan();
  const isExpertPlan = isSubscriptionExpert(plan);
  const {
    fileInputRef,
    fields,
    methods,
    isDialogOpen,
    isAnalyzing,
    isConfirming,
    openFilePicker,
    handleFileSelect,
    remove,
    closeDialog,
    confirm,
  } = useStockAiIntake();

  const watchedItems = useWatch({ control: methods.control, name: 'items' });
  const totalPrice = watchedItems.reduce((sum, item) => sum + (Number(item.totalPrice) || 0), 0);
  const totalItems = fields.length;
  const newItemsCount = fields.filter(item => !item.matchedStockItemId).length;
  const existingItemsCount = totalItems - newItemsCount;
  const expertOnlyMessage = intl.formatMessage({
    id: 'visitDetailGrid.soldItemsRevenueDisabledMessage',
    defaultMessage: 'Dostupné v plánu EXPERT. Aktivujte ve vašem profilu.',
  });
  const button = (
    <Button
      variant="outlined"
      color="secondary"
      startIcon={isAnalyzing ? <CircularProgress size={18} color="inherit" /> : <AutoAwesomeIcon />}
      onClick={openFilePicker}
      disabled={!isExpertPlan || isAnalyzing}
    >
      {isAnalyzing ? (
        <FormattedMessage id="stock.aiIntake.analyzing" defaultMessage="Vyhodnocuji účtenku…" />
      ) : (
        <FormattedMessage id="stock.aiIntake.button" defaultMessage="Naskladnit pomocí AI" />
      )}
    </Button>
  );

  return (
    <>
      {isExpertPlan ? (
        button
      ) : (
        <Tooltip title={expertOnlyMessage}>
          <span>{button}</span>
        </Tooltip>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />

      <Dialog
        open={isDialogOpen}
        onClose={isConfirming ? undefined : closeDialog}
        fullWidth
        maxWidth="md"
        slotProps={{ paper: { sx: { width: { xs: '100vw', md: '80vw' }, m: { xs: 0, md: 2 } } } }}
      >
        <FormProvider {...methods}>
          <DialogTitle>
            <FormattedMessage
              id="stock.aiIntake.dialogTitle"
              defaultMessage="Potvrzení naskladnění"
            />
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2, fontSize: '0.85rem' }}>
              <FormattedMessage
                id="stock.aiIntake.dialogHelper"
                defaultMessage="Zkontrolujte prosím rozpoznané položky a množství. Po potvrzení budou naskladněny."
              />
              <FormattedMessage
                id="stock.aiIntake.dialogHelper.aiWarning"
                defaultMessage="Úpozornění: AI se chová pokaždé jinak, pokud máte hodně nepřesností poříďte fotografii znovu nebo ručně upravte."
              />
            </DialogContentText>

            {totalItems > 0 && (
              <Box
                sx={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 1,
                  mb: 2,
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Stack
                  direction="row"
                  flexWrap="wrap"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Chip
                    size="small"
                    sx={{ fontSize: '0.60rem', fontWeight: 600 }}
                    color="primary"
                    variant="outlined"
                    label={intl.formatMessage(
                      {
                        id: 'stock.aiIntake.summaryItems',
                        defaultMessage: 'Položek: {count}',
                      },
                      { count: totalItems },
                    )}
                  />
                  <Chip
                    size="small"
                    sx={{ fontSize: '0.60rem', fontWeight: 600 }}
                    color="success"
                    variant="outlined"
                    label={intl.formatMessage(
                      {
                        id: 'stock.aiIntake.summaryNew',
                        defaultMessage: 'Nové: {count}',
                      },
                      { count: newItemsCount },
                    )}
                  />
                  <Chip
                    size="small"
                    color="info"
                    sx={{ fontSize: '0.60rem', fontWeight: 600 }}
                    variant="outlined"
                    label={intl.formatMessage(
                      {
                        id: 'stock.aiIntake.summaryExisting',
                        defaultMessage: 'Existující: {count}',
                      },
                      { count: existingItemsCount },
                    )}
                  />

                  <Typography variant="body2" fontSize={'0.85rem'} fontWeight={700}>
                    <FormattedMessage
                      id="stock.aiIntake.summaryTotalPrice"
                      defaultMessage={`Celkem: {price} ${preferredCurrency}`}
                      values={{ price: Math.round(totalPrice) }}
                    />
                  </Typography>
                </Stack>
              </Box>
            )}

            {fields.length === 0 && (
              <Typography color="text.secondary" fontSize="0.85rem">
                <FormattedMessage
                  id="stock.aiIntake.noItems"
                  defaultMessage="Z účtenky se nepodařilo rozpoznat žádné položky."
                />
              </Typography>
            )}

            <Stack spacing={1.5}>
              {fields.map((field, index) => {
                const isNew = !field.matchedStockItemId;

                return (
                  <Box
                    key={field.id}
                    sx={{
                      p: 1.5,
                      border: '1px solid',
                      borderColor: isNew ? 'success.light' : 'info.light',
                      borderLeft: '4px solid',
                      borderLeftColor: isNew ? 'success.main' : 'info.main',
                      borderRadius: 2,
                    }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      spacing={1}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                          <Chip
                            size="small"
                            color={isNew ? 'success' : 'info'}
                            label={
                              isNew
                                ? intl.formatMessage({
                                    id: 'stock.aiIntake.badgeNew',
                                    defaultMessage: 'Nová',
                                  })
                                : intl.formatMessage({
                                    id: 'stock.aiIntake.badgeExisting',
                                    defaultMessage: 'Existující',
                                  })
                            }
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        </Stack>
                        <Typography variant="caption" fontWeight={600}>
                          {watchedItems[index]?.itemName}
                        </Typography>
                        <br />
                        {!isNew && (
                          <Typography variant="caption" color="text.secondary">
                            <FormattedMessage
                              id="stock.aiIntake.matched"
                              defaultMessage="<b>Naskladní se do:</b> {name}"
                              values={{
                                name: field.matchedStockItemName,
                                b: chunks => (
                                  <Box
                                    component="span"
                                    sx={{
                                      fontWeight: 700,
                                      color: 'text.primary',
                                    }}
                                  >
                                    {chunks}
                                  </Box>
                                ),
                              }}
                            />
                          </Typography>
                        )}
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => remove(index)}
                        disabled={isConfirming}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Stack>

                    <Stack direction="row" spacing={0.5} sx={{ mt: 1.5 }}>
                      <TextField
                        fieldPath={`items.${index}.packageCount`}
                        label={intl.formatMessage({
                          id: 'stock.aiIntake.packageCount',
                          defaultMessage: 'Balení',
                        })}
                        size="small"
                        type="number"
                        sx={{ width: 130 }}
                        control={methods.control}
                        disabled={isConfirming}
                      />
                      <TextField
                        fieldPath={`items.${index}.quantity`}
                        fullWidth
                        label={intl.formatMessage(
                          {
                            id: 'stock.aiIntake.quantity',
                            defaultMessage: 'Množství v balení ({unit})',
                          },
                          { unit: field.unit },
                        )}
                        type="number"
                        size="small"
                        control={methods.control}
                        disabled={isConfirming}
                        rules={{
                          validate: value =>
                            !UNITS_WITH_SUSPICIOUS_LOW_QUANTITY.has(field.unit) ||
                            Number(value) > 1 ||
                            'stock.aiIntake.quantityTooLow',
                        }}
                      />
                      <TextField
                        fieldPath={`items.${index}.totalPrice`}
                        label={intl.formatMessage({
                          id: 'stock.aiIntake.totalPrice',
                          defaultMessage: `Cena (${preferredCurrency})`,
                        })}
                        type="number"
                        sx={{ width: 170 }}
                        size="small"
                        control={methods.control}
                        disabled={isConfirming}
                      />
                    </Stack>

                    {isNew && (
                      <Accordion
                        disableGutters
                        elevation={0}
                        sx={{
                          mt: 1,
                          '&:before': { display: 'none' },
                          bgcolor: 'transparent',
                        }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          sx={{
                            minHeight: 40,
                            px: 0,
                            '& .MuiAccordionSummary-content': { my: 0.75 },
                          }}
                        >
                          <Typography variant="body2" fontWeight={600}>
                            <FormattedMessage
                              id="stock.aiIntake.editNewItemDetails"
                              defaultMessage="Upravit údaje nové položky"
                            />
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ px: 0, pt: 0 }}>
                          <Stack spacing={1.5}>
                            <TextField
                              fieldPath={`items.${index}.itemName`}
                              label={
                                <FormattedMessage
                                  id="stockItem.itemName"
                                  defaultMessage="Skladová položka"
                                />
                              }
                              type="text"
                              fullWidth
                              control={methods.control}
                              disabled={isConfirming}
                            />
                            <Box>
                              <Typography variant="caption" color="text.secondary" gutterBottom>
                                <FormattedMessage
                                  id="stockItem.itemType"
                                  defaultMessage="Typ položky"
                                />
                              </Typography>
                              <ToggleButtonGroup
                                control={methods.control}
                                fieldPath={`items.${index}.itemType`}
                                options={[
                                  {
                                    value: 'MATERIAL',
                                    label: (
                                      <FormattedMessage
                                        id="stockItem.material"
                                        defaultMessage="Materiál"
                                      />
                                    ),
                                  },
                                  {
                                    value: 'GOODS',
                                    label: (
                                      <FormattedMessage
                                        id="stockItem.goods"
                                        defaultMessage="Zboží"
                                      />
                                    ),
                                  },
                                ]}
                                fullWidth
                                size="small"
                                disabled={isConfirming}
                              />
                            </Box>
                            <SelectField
                              items={unitList}
                              label={intl.formatMessage({
                                id: 'stockItem.unit',
                                defaultMessage: 'Vyberte jednotku materiálu',
                              })}
                              control={methods.control}
                              fieldPath={`items.${index}.unit`}
                              keyExtractor={unit => unit.name}
                              labelExtractor={unit => unit.name}
                              disabled={isConfirming}
                              size="small"
                            />
                            {watchedItems[index]?.itemType === 'GOODS' && (
                              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                                <TextField
                                  fieldPath={`items.${index}.retailPrice`}
                                  label={
                                    <FormattedMessage
                                      id="stockItem.retailPrice"
                                      defaultMessage={`Prodejní cena (${preferredCurrency})`}
                                    />
                                  }
                                  type="number"
                                  fullWidth
                                  control={methods.control}
                                  disabled={isConfirming}
                                />
                                <TextField
                                  fieldPath={`items.${index}.brand`}
                                  label={
                                    <FormattedMessage
                                      id="stockItem.brand"
                                      defaultMessage="Značka"
                                    />
                                  }
                                  type="text"
                                  fullWidth
                                  control={methods.control}
                                  disabled={isConfirming}
                                />
                              </Stack>
                            )}
                            <TextField
                              fieldPath={`items.${index}.threshold`}
                              label={intl.formatMessage({
                                id: 'stockItem.minimumPackageCount',
                                defaultMessage: 'Minimální počet balení',
                              })}
                              type="number"
                              fullWidth
                              control={methods.control}
                              disabled={isConfirming}
                            />
                          </Stack>
                        </AccordionDetails>
                      </Accordion>
                    )}
                  </Box>
                );
              })}
            </Stack>
          </DialogContent>
          <Divider />
          <DialogActions>
            <Button onClick={closeDialog} disabled={isConfirming}>
              <FormattedMessage id="stock.aiIntake.cancel" defaultMessage="Zrušit" />
            </Button>
            <Button
              variant="contained"
              onClick={methods.handleSubmit(confirm)}
              disabled={isConfirming || fields.length === 0}
              startIcon={isConfirming ? <CircularProgress size={18} color="inherit" /> : undefined}
            >
              <FormattedMessage
                id="stock.aiIntake.confirm"
                defaultMessage="Potvrdit a naskladnit"
              />
            </Button>
          </DialogActions>
        </FormProvider>
      </Dialog>
    </>
  );
};

export default StockAiIntake;
